import { Paper, Typography, Stack, Chip, Button, Avatar, Divider, Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from "@mui/icons-material/Delete";
import { useSelector } from 'react-redux';
import { selectUserData, selectUserToken, setDisplayErrorSnackBar } from '../../../../features/userSlice';
import { CheckPermissions } from '../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ActivityNotesEndPointLogic } from '../../../../logic/AssetManagement/ActivityNotes/ActivityNotesLogic/ActivityNotesEndPointLogic';
import { useDispatch } from 'react-redux';
import { EditNote } from '../ActivityNotesModals/EditNote';
import CommentsAndFiles from '../../../Discussion/CommentsAndFiles';
import ActivityNoteDetailsSkeleton from './ActivityNoteDetailsSkeleton';

const ActivityNoteDetails = () => {
  const userData = useSelector(selectUserData);
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [, setDeleteSuccess] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activityNoteLoaded, setActivityNoteLoaded] = useState(false);
  const [commentsFilesInitialized, setCommentsFilesInitialized] = useState(false);
  const singleNoteData = useSelector((state: any) => state.activityNotes.singleNoteData);
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [, setIsThisEdit] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const hasDeleteActivityNotesPermission = CheckPermissions(
    userData?.data?.products?.assets,
    "overview",
    "delete_activity_notes"
  );
  const hasEditActivityNotesPermission = CheckPermissions(
    userData?.data?.products?.assets,
    "overview",
    "edit_activity_notes"
  );
  const noteShareUid = searchParams.get("noteShareUid");
  
  // Memoize noteShareUid to prevent unnecessary re-renders and duplicate API calls
  const memoizedNoteShareUid = useMemo(() => {
    return noteShareUid && noteShareUid !== "undefined" ? noteShareUid : null;
  }, [noteShareUid]);
  
  const response = useSelector(selectUserData);
  const siteNames = response?.data?.website_permissions?.map((permission: any) => {
    const key = Object.keys(permission)[0];
    return {
      site: permission[key],
      key: key,
    };
  });

  // Get the activity note data from Redux store
  const getActivityNoteData = () => {
    if (!singleNoteData || Object.keys(singleNoteData).length === 0) return null;

    const firstKey = Object.keys(singleNoteData)[0];
    const noteArray = singleNoteData[firstKey];

    if (Array.isArray(noteArray) && noteArray.length > 0) {
      return noteArray[0];
    }
    return null;
  };

  const activityNote = getActivityNoteData();
  const selected = searchParams.get('selected');

  // If selected query param exists, then after the component unmount, navigate to the activity notes page with the selected query param
  useEffect(() => {
    return () => {
      if (selected && selected.length > 0) {
        navigate(`/tools/activity_notes?selected=${selected}`);
      } else {
        navigate("/tools/activity_notes");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (memoizedNoteShareUid && oauthTokenHeader) {
      const fetchSingleActivityNote = async () => {
        setLoading(true);
        setActivityNoteLoaded(false);
        try {
          await ActivityNotesEndPointLogic.getSingleActivityNoteData(oauthTokenHeader, memoizedNoteShareUid, dispatch);
          setActivityNoteLoaded(true);
        } catch (error: any) {
          dispatch(setDisplayErrorSnackBar({ message: error?.parsedText || "Error while fetching data. Please try again later.", type: "error", }));
        } finally {
          setLoading(false);
        }
      }
      fetchSingleActivityNote();
    } else {
      setActivityNoteLoaded(false);
      setLoading(false); // Set loading to false when there's no valid noteShareUid
    }
  }, [oauthTokenHeader, memoizedNoteShareUid, dispatch]);

  // Initialize CommentsAndFiles only once when activityNote is loaded
  useEffect(() => {
    if (activityNoteLoaded && memoizedNoteShareUid && !commentsFilesInitialized) {
      setCommentsFilesInitialized(true);
    } else if (!memoizedNoteShareUid) {
      setCommentsFilesInitialized(false);
    }
  }, [activityNoteLoaded, memoizedNoteShareUid, commentsFilesInitialized]);

  const handleDeleteNote = async () => {
    setDeleteConfirmOpen(false); // Close the confirmation dialog
    setLoading(true);
    try {
      await ActivityNotesEndPointLogic.deleteActivityNote(oauthTokenHeader, activityNote?.shared_uid, setDeleteSuccess);
      if (oauthTokenHeader) {
        await ActivityNotesEndPointLogic.getActivityNotes(oauthTokenHeader, dispatch);
      } else {
        throw new Error("No token found");
      }
    } catch (error: any) {
      dispatch(setDisplayErrorSnackBar({ message: error?.parsedText || "Error while fetching data. Please try again later.", type: "error", }));
    } finally {
      setLoading(false);
      // Preserve the 'selected' query param if it exists
      if (selected && selected.length > 0) {
        navigate(`/tools/activity_notes?selected=${selected}`);
      } else {
        navigate("/tools/activity_notes");
      }
    }
  };

  const handleEditClick = async (_note: string) => {
    await ActivityNotesEndPointLogic.getAllLabels(oauthTokenHeader, dispatch);
    setIsThisEdit(true);
    setIsDialogOpen(true);
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("edit", "true");
      return updatedParams;
    });
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setIsThisEdit(false);
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.delete("create"); // Remove "create=true"
      updatedParams.delete("edit");
      updatedParams.delete("shareuid"); // Remove "edit=true"
      return updatedParams;
    });
  }

  const relatedAsset = siteNames.find((source: any) => source.key === Object.keys(singleNoteData)[0])?.site?.ui_site_name || "";

  return (
    <>
      {(loading && memoizedNoteShareUid) ? (
        <ActivityNoteDetailsSkeleton />
      ) : (
        <Box sx={{ width: "100%", marginTop: 4 }}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2, width: "100%" }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ flex: 1 }}
              >
                <Typography variant="h6">
                  {activityNote?.note?.length > 100 ? activityNote.note.slice(0, 100) + "..." : activityNote?.note}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {(() => {
                    try {
                      const labels = typeof activityNote?.labels === 'string'
                        ? JSON.parse(activityNote.labels)
                        : activityNote?.labels;
                      return Array.isArray(labels) ? labels.map(
                        (label: string, index: number) => (
                          <Chip
                            key={index}
                            label={label}
                            size="small"
                            color="secondary"
                          />
                        )
                      ) : null;
                    } catch {
                      return null;
                    }
                  })()}
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1}>
                {hasDeleteActivityNotesPermission ? (
                  <Button
                    color="secondary"
                    data-tracker="103"
                    onClick={() => setDeleteConfirmOpen(true)}
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    <DeleteIcon />
                  </Button>
                ) : (
                  <DeleteIcon color="disabled" />
                )}
                {hasEditActivityNotesPermission ? (
                  <Button
                    variant="contained"
                    color="secondary"
                    endIcon={<EditIcon />}
                    onClick={() => handleEditClick("")}
                    data-tracker="418"
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    Edit Note
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    data-tracker="418"
                    endIcon={<EditIcon />}
                    sx={{ textTransform: "none" }}
                    disabled
                  >
                    Edit Note
                  </Button>
                )}
              </Stack>
            </Stack>
            <Stack direction="row" spacing={3} flexWrap="wrap" mb={2} alignItems="center">
              <Typography variant="body2" sx={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontWeight: 500 }}>Related Asset: </span>{" "}
                {relatedAsset.charAt(0).toUpperCase() + relatedAsset.slice(1)}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={3}>
                <Typography variant="body2">
                  <span style={{ fontWeight: 500 }}>Stakeholder:</span> {activityNote?.note_type || "N/A"}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">
                    <span style={{ fontWeight: 500 }}>Created By:</span>
                  </Typography>
                  <Avatar sx={{ width: 24, height: 24 }}>
                    {activityNote?.created_by?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <Typography variant="body2">
                    {activityNote?.created_by}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2">
                  <span style={{ fontWeight: 500 }}>Date:</span>{" "}
                  {activityNote?.activity_date ? new Date(activityNote.activity_date).toLocaleDateString("en-GB") : ""}
                </Typography>
              </Stack>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {activityNote?.details || ""}
            </Typography>
          </Paper>
        </Box>
      )}
      {commentsFilesInitialized && (
        <CommentsAndFiles
          key={`comments-files-${memoizedNoteShareUid}`}
          objectType="business_unit_activity_notes"
          referenceModule="Assets Activity Notes"
          moduleType="activityNotes"
          objectSharedUid={memoizedNoteShareUid}
          postCommentTrackID="451"
          reactionTrackID="460"
          uploadFileTrackID="461"
          downloadFileTrackID="462"
          previewFileTrackID="518"
        />
      )}
      <EditNote
        open={isDialogOpen}
        handleClose={handleCloseDialog}
        activityNote={activityNote}
        relatedAsset={relatedAsset}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Activity Note
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this note? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteNote}
            color="secondary"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ActivityNoteDetails;
